import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FieldWrapper } from '@ngx-formly/core';
import { MatIcon } from '@angular/material/icon';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { merge, takeUntil } from 'rxjs';
import { ClearFieldButtonComponent } from '../../../../../../../shared/components/formly-fields/clear-field-button.component';
import {
  buildClearAriaLabel,
  clearControl,
  clearableIsDisabled,
  controlHasClearableValue,
} from '../../../../../../../shared/components/formly-fields/clearable-field.type';

@Component({
  selector: 'formly-wrapper-addons',
  template: `
    <ng-template #matPrefix>
      @if (props.addonLeft) {
        <span class="text-md text-gray-500 px-2 inline-block ">
          @if (props.addonLeft.icon) {
            <mat-icon>{{ props.addonLeft.icon }}</mat-icon>
          }
          @if (props.addonLeft.text) {
            <span>{{ props.addonLeft.text }}</span>
          }
        </span>
      }
    </ng-template>
    
    <ng-container #fieldComponent></ng-container>
    
    <ng-template #matSuffix>
      <span class="dd-clear-suffix dd-addon-suffix">
        @if (props.addonRight) {
          <span class="dd-addon-suffix__passive text-md text-gray-500 px-2 inline-block">
          @if (props.addonRight.icon) {
            <mat-icon>{{ props.addonRight.icon }}</mat-icon>
          }
          @if (props.addonRight.text) {
            <span>{{ props.addonRight.text }}</span>
          }
          </span>
        }
        <!-- Handoff #20830: plain typed addon fields carry no X. The button only renders when
             the field also has the 'clear' wrapper — a native select, or an admin who opted in
             through the 'Wisknop' addon (wrapper-config.ts). This wrapper stays the sole owner
             of the suffix template either way. No [readOnly] here any more (task 21258c): read
             mode's ReadonlyWrapper sits OUTSIDE this whole tree and replaces the field before
             #fieldComponent is ever built, so this suffix only ever renders while editable. -->
        @if (hasClearWrapper()) {
          <app-clear-field-button
            [hasValue]="hasValue()"
            [disabled]="isDisabled()"
            [ariaLabel]="clearAriaLabel()"
            (clearSelection)="clear()" />
        }
      </span>
    </ng-template>
    `,
  imports: [ClearFieldButtonComponent, MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyWrapperAddons extends FieldWrapper implements OnInit {
  @ViewChild('matPrefix', { static: true }) matPrefix!: TemplateRef<any>;
  @ViewChild('matSuffix', { static: true }) matSuffix!: TemplateRef<any>;
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #destroy$ = injectDestroy();

  ngOnInit() {
    if (this.matPrefix) {
      // Note: for text use `textPrefix` property (only in Angular Material >= 15)
      this.props.prefix = this.matPrefix;
    }

    if (this.matSuffix) {
      // Note: for text use `textSuffix` property (only in Angular Material >= 15)
      this.props.suffix = this.matSuffix;
    }

    merge(this.formControl.valueChanges, this.formControl.statusChanges)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(() => this.#changeDetectorRef.markForCheck());
  }

  hasClearWrapper(): boolean {
    return !!this.field.wrappers?.includes('clear');
  }

  // A FieldWrapper cannot extend ClearableFieldType (single inheritance, Formly's FieldWrapper
  // is the required base), so the contract is delegated to the shared functions instead.
  hasValue(): boolean {
    return controlHasClearableValue(this.formControl);
  }

  isDisabled(): boolean {
    return clearableIsDisabled(this.formControl, this.props);
  }

  clearAriaLabel(): string {
    return buildClearAriaLabel(this.props?.label);
  }

  clear(): void {
    clearControl(this.formControl, this.props);
  }
}
